import { useSetting } from '@work4all/data/lib/settings';
import {
  EntityPickerColumnsVisibilityItem,
  EntityPickerSettings,
  filterPickerEntities,
  getGetPickerColumns,
  sortPickerEntities,
  visiblePickerColumns,
} from '@work4all/data/lib/settings/settings';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

export type PickerSettingsSortBy = 'name' | 'number';

const commonSortBy = ['name', 'number'];

export const getGridSizes = (
  columns: EntityPickerColumnsVisibilityItem[]
): string =>
  columns
    .filter((column) => column.isVisible)
    .map((column) => column.width)
    .join(' ');

const settingsItemsKeyMap = {
  [Entities.customer]: {
    filter: 'extinct',
    sort: commonSortBy,
    columns: [],
  },
  [Entities.supplier]: {
    filter: 'extinct',
    sort: commonSortBy,
    columns: [],
  },
  [Entities.article]: {
    filter: 'isShutDown',
    sort: commonSortBy,
    columns: [],
  },
  [Entities.contract]: {
    filter: 'isClosed',
    sort: null,
    columns: getGetPickerColumns(Entities.contract),
  },
  [Entities.deliveryNote]: {
    filter: 'isClosed',
    sort: null,
    columns: getGetPickerColumns(Entities.deliveryNote),
  },
  [Entities.inboundDeliveryNote]: {
    filter: 'isClosed',
    sort: null,
    columns: getGetPickerColumns(Entities.inboundDeliveryNote),
  },
  [Entities.order]: {
    filter: 'isClosed',
    sort: null,
    columns: getGetPickerColumns(Entities.order),
  },
  // ...
  // Add new sort/filter/columns property for new entity pickers here
};

export function usePickerSettings(entity: Entities) {
  const settings: EntityPickerSettings = settingsItemsKeyMap[entity]
    ? {
        filter: {
          options: [
            {
              key: settingsItemsKeyMap[entity].filter,
              query: { $eq: 0 },
            },
          ],
        },
        sort: {
          options: settingsItemsKeyMap[entity].sort,
        },
        columns: {
          options: settingsItemsKeyMap[entity].columns?.map(
            (column: EntityPickerColumnsVisibilityItem) => ({
              key: column.key,
              width: column.width,
              isVisible: false,
            })
          ),
        },
      }
    : null;

  const { value: sortedPickerEntities, set: setSortedPickerEntities } =
    useSetting(
      sortPickerEntities({
        entityType: entity,
        defaultValue: settings?.sort.options?.[0],
      })
    );

  const { value: filteredEntitiesSetting, set: setHideClosedPickerEntities } =
    useSetting(filterPickerEntities({ entityType: entity }));

  const {
    value: visiblePickerColumnsSetting,
    set: setVisiblePickerColumnsSetting,
  } = useSetting(visiblePickerColumns({ entityType: entity }));

  return {
    settings,
    filteredEntitiesSetting,
    setHideClosedPickerEntities,
    sortedPickerEntities:
      entity === Entities.articlePurchaseInformation
        ? 'price'
        : sortedPickerEntities,
    setSortedPickerEntities,
    visiblePickerColumnsSetting,
    setVisiblePickerColumnsSetting,
  };
}
